/**
 * ImageUpload — Drag & drop image upload with optional AI analysis
 *
 * Client component that handles:
 * - Drag & drop + click-to-browse file selection
 * - Image preview with remove capability
 * - Upload to /api/upload endpoint
 * - Optional AI analysis via /api/analyze-image (Gemini Flash)
 * - Exposes uploadImages() via forwardRef for parent form integration
 *
 * @module components/image-upload
 */

"use client";

import {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useRef,
    useState,
} from "react";
import Image from "next/image";
import { ImagePlus, Loader2, Sparkles, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/* -------------------------------------------------------------------------- */
/*  Types                                                                      */
/* -------------------------------------------------------------------------- */

/** Result returned by the AI analysis endpoint */
export interface AnalysisResult {
    title: string;
    description: string;
    condition: string;
    categorySlug: string;
    suggestedPrice?: number | null;
    suggestedType?: string;
    brand?: string | null;
    estimatedWeight?: string | null;
}

/** Handle exposed to parent components via ref */
export interface ImageUploadHandle {
    /** Upload all pending (local) images and return their URLs */
    uploadImages: () => Promise<string[]>;
    /** Whether the component has any images (pending or uploaded) */
    hasImages: () => boolean;
}

/** Internal representation of an image in the upload queue */
interface ImageItem {
    /** Unique client-side ID */
    id: string;
    /** Local blob URL for preview */
    previewUrl: string;
    /** Original File object (null if already uploaded) */
    file: File | null;
    /** Server URL after upload (e.g. /uploads/abc123.jpg) */
    uploadedUrl: string | null;
    /** Whether this image is currently being uploaded */
    uploading: boolean;
}

/** Props for the ImageUpload component */
interface ImageUploadProps {
    /** Callback when AI analysis completes */
    onAnalysis?: (result: AnalysisResult) => void;
    /** Callback when the list of uploaded URLs changes */
    onImagesChange?: (urls: string[]) => void;
    /** Maximum number of images allowed */
    maxImages?: number;
    /** Disable all interactions */
    disabled?: boolean;
}

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

/** Allowed MIME types */
const ALLOWED_TYPES = new Set([
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/heic",
]);

/** Maximum file size in bytes (5 MB) */
const MAX_FILE_SIZE = 5 * 1024 * 1024;

/** Generate a random client-side ID */
function generateId(): string {
    return `img-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

/* -------------------------------------------------------------------------- */
/*  Component                                                                  */
/* -------------------------------------------------------------------------- */

export const ImageUpload = forwardRef<ImageUploadHandle, ImageUploadProps>(
    function ImageUpload(
        { onAnalysis, onImagesChange, maxImages = 5, disabled = false },
        ref,
    ) {
        const [images, setImages] = useState<ImageItem[]>([]);
        const [isDragging, setIsDragging] = useState(false);
        const [isAnalyzing, setIsAnalyzing] = useState(false);
        const fileInputRef = useRef<HTMLInputElement>(null);

        /* ------------------------------------------------------------------ */
        /*  Validate & add files                                               */
        /* ------------------------------------------------------------------ */

        const addFiles = useCallback(
            (files: FileList | File[]) => {
                const fileArray = Array.from(files);
                const newItems: ImageItem[] = [];

                for (const file of fileArray) {
                    // Validate type
                    if (!ALLOWED_TYPES.has(file.type)) {
                        toast.error(
                            `Type non supporté : ${file.name}. Formats : JPEG, PNG, WebP, HEIC`,
                        );
                        continue;
                    }
                    // Validate size
                    if (file.size > MAX_FILE_SIZE) {
                        toast.error(`Fichier trop volumineux (max 5 Mo) : ${file.name}`);
                        continue;
                    }

                    newItems.push({
                        id: generateId(),
                        previewUrl: URL.createObjectURL(file),
                        file,
                        uploadedUrl: null,
                        uploading: false,
                    });
                }

                if (newItems.length === 0) return;

                setImages((prev) => {
                    const remaining = maxImages - prev.length;
                    if (remaining <= 0) {
                        toast.error(`Maximum ${maxImages} photos autorisées`);
                        return prev;
                    }
                    const toAdd = newItems.slice(0, remaining);
                    if (newItems.length > remaining) {
                        toast.warning(
                            `Seules ${remaining} photo(s) ajoutée(s) (max ${maxImages})`,
                        );
                    }
                    return [...prev, ...toAdd];
                });
            },
            [maxImages],
        );

        /* ------------------------------------------------------------------ */
        /*  Remove image                                                       */
        /* ------------------------------------------------------------------ */

        const removeImage = useCallback(
            (id: string) => {
                setImages((prev) => {
                    const item = prev.find((img) => img.id === id);
                    if (item) {
                        URL.revokeObjectURL(item.previewUrl);
                    }
                    const next = prev.filter((img) => img.id !== id);
                    return next;
                });
                // Notify parent — compute from latest state after render
                setTimeout(() => {
                    setImages((current) => {
                        const urls = current
                            .map((img) => img.uploadedUrl)
                            .filter((u): u is string => !!u);
                        onImagesChange?.(urls);
                        return current;
                    });
                }, 0);
            },
            [onImagesChange],
        );

        /* ------------------------------------------------------------------ */
        /*  Upload images to server                                            */
        /* ------------------------------------------------------------------ */

        const uploadImages = useCallback(async (): Promise<string[]> => {
            // Find images that haven't been uploaded yet
            const pendingImages = images.filter(
                (img) => img.file && !img.uploadedUrl,
            );

            // If nothing to upload, return already-uploaded URLs
            if (pendingImages.length === 0) {
                return images
                    .map((img) => img.uploadedUrl)
                    .filter((u): u is string => !!u);
            }

            // Mark images as uploading
            setImages((prev) =>
                prev.map((img) =>
                    img.file && !img.uploadedUrl
                        ? { ...img, uploading: true }
                        : img,
                ),
            );

            // Build FormData with pending files
            const formData = new FormData();
            for (const img of pendingImages) {
                if (img.file) {
                    formData.append("images", img.file);
                }
            }

            // Upload to server
            const response = await fetch("/api/upload", {
                method: "POST",
                body: formData,
            });

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(
                    err.error ?? `Erreur upload (${response.status})`,
                );
            }

            const results: Array<{ url: string; filename: string }> =
                await response.json();

            // Compute allUrls OUTSIDE setImages to avoid React 19 batching issue
            // where the updater runs asynchronously and the returned value is stale
            let resultIdx = 0;
            const allUrls: string[] = images.map((img) => {
                if (!img.uploadedUrl && img.file && resultIdx < results.length) {
                    return results[resultIdx++].url;
                }
                return img.uploadedUrl!;
            });

            // Update state for UI display (separate from return value)
            setImages((prev) => {
                let rIdx = 0;
                return prev.map((img) => {
                    if (!img.uploadedUrl && img.file && rIdx < results.length) {
                        return {
                            ...img,
                            uploadedUrl: results[rIdx++].url,
                            uploading: false,
                            file: null,
                        };
                    }
                    return { ...img, uploading: false };
                });
            });

            // Notify parent
            onImagesChange?.(allUrls.filter((u): u is string => !!u));

            return allUrls.filter((u): u is string => !!u);
        }, [images, onImagesChange]);

        /* ------------------------------------------------------------------ */
        /*  Expose handle to parent via ref                                    */
        /* ------------------------------------------------------------------ */

        useImperativeHandle(
            ref,
            () => ({
                uploadImages,
                hasImages: () => images.length > 0,
            }),
            [uploadImages, images.length],
        );

        /* ------------------------------------------------------------------ */
        /*  AI Analysis                                                        */
        /* ------------------------------------------------------------------ */

        const analyzeWithAI = useCallback(async () => {
            // Find the first image with a file to analyze
            const imageToAnalyze = images.find((img) => img.file);
            if (!imageToAnalyze?.file) {
                toast.error("Ajoutez une photo pour l'analyse IA");
                return;
            }

            setIsAnalyzing(true);

            try {
                // First upload images so they're saved
                const uploadedUrls = await uploadImages();
                if (uploadedUrls.length === 0) {
                    toast.error("Erreur lors de l'envoi des photos");
                    return;
                }

                // Send first image for AI analysis
                const formData = new FormData();
                formData.append("image", imageToAnalyze.file);

                const response = await fetch("/api/analyze-image", {
                    method: "POST",
                    body: formData,
                });

                if (!response.ok) {
                    const err = await response.json().catch(() => ({}));
                    toast.error(
                        err.error ?? "Erreur lors de l'analyse IA",
                    );
                    return;
                }

                const result: AnalysisResult = await response.json();
                onAnalysis?.(result);
                toast.success("Analyse IA terminée — champs pré-remplis !");
            } catch (error) {
                console.error("[ImageUpload] AI analysis error:", error);
                toast.error("Erreur lors de l'analyse. Réessayez.");
            } finally {
                setIsAnalyzing(false);
            }
        }, [images, uploadImages, onAnalysis]);

        /* ------------------------------------------------------------------ */
        /*  Drag & Drop handlers                                               */
        /* ------------------------------------------------------------------ */

        const handleDragOver = useCallback(
            (e: React.DragEvent) => {
                e.preventDefault();
                e.stopPropagation();
                if (!disabled) setIsDragging(true);
            },
            [disabled],
        );

        const handleDragLeave = useCallback((e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);
        }, []);

        const handleDrop = useCallback(
            (e: React.DragEvent) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragging(false);
                if (!disabled && e.dataTransfer.files.length > 0) {
                    addFiles(e.dataTransfer.files);
                }
            },
            [disabled, addFiles],
        );

        const handleFileSelect = useCallback(
            (e: React.ChangeEvent<HTMLInputElement>) => {
                if (e.target.files && e.target.files.length > 0) {
                    addFiles(e.target.files);
                }
                // Reset input so re-selecting same file works
                e.target.value = "";
            },
            [addFiles],
        );

        /* ------------------------------------------------------------------ */
        /*  Render                                                             */
        /* ------------------------------------------------------------------ */

        const hasImages = images.length > 0;
        const canAddMore = images.length < maxImages;

        return (
            <div className="space-y-4">
                {/* Drop zone */}
                {canAddMore && (
                    <div
                        role="button"
                        tabIndex={0}
                        aria-label="Zone de dépôt d'images — cliquez ou glissez-déposez"
                        className={cn(
                            "relative flex min-h-[160px] cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-6 transition-colors",
                            isDragging
                                ? "border-primary bg-primary/5"
                                : "border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50",
                            disabled && "pointer-events-none opacity-50",
                        )}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                fileInputRef.current?.click();
                            }
                        }}
                    >
                        <Upload
                            className="h-8 w-8 text-muted-foreground"
                            aria-hidden="true"
                        />
                        <div className="text-center">
                            <p className="text-sm font-medium">
                                Glissez vos photos ici ou cliquez pour parcourir
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">
                                JPEG, PNG, WebP • Max 5 Mo par photo •{" "}
                                {images.length}/{maxImages} photos
                            </p>
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/heic"
                            multiple
                            className="sr-only"
                            onChange={handleFileSelect}
                            tabIndex={-1}
                            aria-hidden="true"
                        />
                    </div>
                )}

                {/* Image previews */}
                {hasImages && (
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                        {images.map((img) => (
                            <div
                                key={img.id}
                                className="group relative aspect-square overflow-hidden rounded-lg border bg-muted"
                            >
                                <Image
                                    src={img.previewUrl}
                                    alt="Photo ajoutée"
                                    fill
                                    className="object-cover"
                                    sizes="(max-width: 640px) 50vw, (max-width: 768px) 33vw, 25vw"
                                />
                                {/* Upload progress overlay */}
                                {img.uploading && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                                        <Loader2
                                            className="h-6 w-6 animate-spin text-white"
                                            aria-label="Envoi en cours"
                                        />
                                    </div>
                                )}
                                {/* Uploaded badge */}
                                {img.uploadedUrl && !img.uploading && (
                                    <div className="absolute left-2 top-2">
                                        <span className="inline-flex items-center rounded-full bg-green-500/90 px-2 py-0.5 text-xs font-medium text-white">
                                            ✓
                                        </span>
                                    </div>
                                )}
                                {/* Remove button */}
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        removeImage(img.id);
                                    }}
                                    className="absolute right-2 top-2 rounded-full bg-destructive/80 p-1.5 text-white opacity-0 transition-opacity hover:bg-destructive group-hover:opacity-100 focus:opacity-100"
                                    aria-label={`Supprimer la photo`}
                                    disabled={disabled || img.uploading}
                                >
                                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                                </button>
                            </div>
                        ))}

                        {/* Add more button */}
                        {canAddMore && (
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="flex aspect-square items-center justify-center rounded-lg border-2 border-dashed border-muted-foreground/25 transition-colors hover:border-primary/50 hover:bg-muted/50"
                                aria-label="Ajouter une photo"
                                disabled={disabled}
                            >
                                <ImagePlus
                                    className="h-6 w-6 text-muted-foreground"
                                    aria-hidden="true"
                                />
                            </button>
                        )}
                    </div>
                )}

                {/* AI Analysis button */}
                {hasImages && (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={analyzeWithAI}
                        disabled={disabled || isAnalyzing}
                        className="gap-2"
                    >
                        {isAnalyzing ? (
                            <>
                                <Loader2
                                    className="h-4 w-4 animate-spin"
                                    aria-hidden="true"
                                />
                                Analyse en cours…
                            </>
                        ) : (
                            <>
                                <Sparkles className="h-4 w-4" aria-hidden="true" />
                                Analyse IA (pré-remplir)
                            </>
                        )}
                    </Button>
                )}
            </div>
        );
    },
);

ImageUpload.displayName = "ImageUpload";
